import { router, useLocalSearchParams } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { goBack } from '@/components/BackChevron';
import { BrandTile } from '@/components/BrandTile';
import { Heading } from '@/components/Heading';
import { PinInput, type PinInputHandle } from '@/components/PinInput';
import { SUPPORT_EMAIL } from '@/constants/api';
import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import { openEmail } from '@/lib/openEmail';
import { safeInternalPath } from '@/lib/safeInternalPath';
import { isApiError, RateLimitError } from '@/services/errors';
import { trackCompleteRegistration } from '@/services/metaPixel';
import { authStore } from '@/stores/auth.store';

const RESEND_COOLDOWN_S = 60;

const VerifyScreen = observer(() => {
  const { contact, channel, redirect } = useLocalSearchParams<{
    contact: string;
    channel: 'sms' | 'email';
    redirect?: string;
  }>();
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(RESEND_COOLDOWN_S);
  const [resending, setResending] = useState(false);
  const [emailFallback, setEmailFallback] = useState<'idle' | 'sending' | 'sent'>('idle');
  const pinRef = useRef<PinInputHandle>(null);

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const id = setInterval(() => setSecondsRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsRemaining]);

  async function handleComplete(value: string) {
    // submitting guard: PinInput fires onComplete on every 6-digit value
    // change, so a paste-then-edit can call this twice while the first
    // verify is in flight — double-submitting the PIN (and pre-guard,
    // double-firing registration analytics).
    if (!contact || submitting) return;
    setSubmitting(true);
    setErrorCode(null);
    setErrorMessage(null);
    try {
      const { newUser } = await authStore.verifyPin(contact, value);
      // Meta Pixel CompleteRegistration — only on the account's FIRST
      // verified login (server's new_user flag covers both PIN-only
      // signups and public-signup users signing in for the first time).
      // Returning logins must not fire it, or Meta registration counts
      // inflate with every login.
      // Uses authStore.user (populated by verifyPin) so we include
      // user_id for stronger CAPI matching. Email/phone may be null for
      // legacy users created before email capture; that's OK,
      // server-side fallback is user_id + IP + UA.
      const user = authStore.user;
      if (newUser && user) {
        trackCompleteRegistration({
          userId: String(user.id),
          email: user.email ?? undefined,
          phone: user.phone_e164 ?? undefined,
        });
      }
      router.replace(safeInternalPath(redirect));
    } catch (err) {
      setPin('');
      pinRef.current?.focus();
      if (isApiError(err)) {
        setErrorCode(err.code);
        setErrorMessage(messageForCode(err.code, err.message));
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!contact || !channel || secondsRemaining > 0) return;
    setResending(true);
    setErrorMessage(null);
    setErrorCode(null);
    try {
      await authStore.requestPin(channel, contact);
      setSecondsRemaining(RESEND_COOLDOWN_S);
    } catch (err) {
      if (isApiError(err)) setErrorMessage(err.message);
      else setErrorMessage('Could not resend. Please try again.');
    } finally {
      setResending(false);
    }
  }

  // A carrier can accept a text and then refuse to deliver it — T-Mobile's
  // A2P daily cap rejects silently, minutes after the send returns success.
  // `Resend` cannot fix that: every retry hits the same cap for the rest of
  // the day, and the same is true of a landline or a mistyped number. Email
  // is the only channel that routes around a carrier, so it has to be one tap
  // from the screen where people actually get stuck.
  async function handleEmailFallback() {
    if (!contact || emailFallback !== 'idle') return;
    setEmailFallback('sending');
    setErrorMessage(null);
    setErrorCode(null);
    try {
      await authStore.requestPinByEmail(contact);
    } catch (err) {
      setEmailFallback('idle');
      // The endpoint answers ok for every input by design, so the only two
      // throws are a throttle and a dead connection. They need different
      // copy: telling a rate-limited user to check their connection sends
      // them to fix a problem they do not have, on the one screen that was
      // supposed to rescue them.
      if (err instanceof RateLimitError) {
        setErrorMessage(
          `Too many code requests. Try again in ${err.retryAfterSeconds}s, or use "Need help?" below.`,
        );
      } else {
        setErrorMessage('Could not reach MarketPryce. Check your connection and try again.');
      }
      return;
    }
    setEmailFallback('sent');
  }

  const lockedOut = errorCode === 'pin_locked';

  return (
    <View style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.flex}
        >
          <ScrollView
            contentContainerStyle={styles.scroll}
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.cardWrap}>
              <View style={styles.card}>
                <View style={styles.brand}>
                  <BrandTile size={56} combo="frostOnElectric" style={styles.brandTile} />
                  <Heading size="4xl" tone="inverse" style={styles.heading}>
                    Enter code
                  </Heading>
                  <Text style={styles.subheading}>
                    Sent to {contact ? maskContact(contact) : 'your device'}.
                  </Text>
                </View>

                <View style={styles.pinWrap}>
                  <PinInput
                    ref={pinRef}
                    value={pin}
                    onChange={setPin}
                    onComplete={handleComplete}
                    hasError={Boolean(errorCode)}
                    autoFocus
                    editable={!submitting && !lockedOut}
                  />
                </View>

                {submitting && <ActivityIndicator color={colors.primaryFg} style={styles.spinner} />}

                {errorMessage && <Text style={styles.error}>{errorMessage}</Text>}

                <View style={styles.actions}>
                  {!lockedOut && (
                    <Pressable
                      onPress={handleResend}
                      disabled={secondsRemaining > 0 || resending}
                      style={styles.resendButton}
                      hitSlop={8}
                      accessibilityRole="button"
                      accessibilityLabel="Resend code"
                      accessibilityState={{ disabled: secondsRemaining > 0 || resending }}
                    >
                      <Text
                        style={[
                          styles.resend,
                          (secondsRemaining > 0 || resending) && styles.resendDisabled,
                        ]}
                      >
                        {secondsRemaining > 0
                          ? `Resend code in ${secondsRemaining}s`
                          : resending
                            ? 'Resending…'
                            : 'Resend code'}
                      </Text>
                    </Pressable>
                  )}

                  {channel === 'sms' &&
                    (emailFallback === 'sent' ? (
                      <Text style={styles.emailSent}>
                        Check your email. If there’s one on your account, your code is on the way.
                      </Text>
                    ) : (
                      <Pressable
                        onPress={handleEmailFallback}
                        disabled={emailFallback === 'sending'}
                        style={styles.changeButton}
                        hitSlop={8}
                        accessibilityRole="button"
                        accessibilityLabel="Get the code by email instead"
                        accessibilityState={{ disabled: emailFallback === 'sending' }}
                      >
                        {/*
                          The muted lead-in is load-bearing, not decoration.
                          Without it this sits directly under "Resend code" in
                          the same green at the same weight, reading as a
                          second, equal CTA — and the one on top is the one
                          that cannot work for a carrier-blocked user. The
                          qualifier answers "which of these is mine?" without
                          two greens fighting for the same job.
                        */}
                        <Text style={styles.emailFallback}>
                          {emailFallback === 'sending' ? (
                            'Sending…'
                          ) : (
                            <>
                              <Text style={styles.emailFallbackLead}>Still nothing? </Text>
                              Get the code by email instead
                            </>
                          )}
                        </Text>
                      </Pressable>
                    ))}

                  <Pressable
                    onPress={() => goBack('/(auth)/login')}
                    style={styles.changeButton}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel={
                      channel === 'sms' ? 'Try a different number' : 'Try a different email'
                    }
                  >
                    <Text style={styles.change}>
                      {channel === 'sms' ? 'Try a different number' : 'Try a different email'}
                    </Text>
                  </Pressable>

                  {/*
                    Auth-flow-redesign: PinService swallows sender failures
                    by design (no deliverability oracle). Stranded users
                    with a typo'd phone or a Twilio outage have no way out
                    without this — `Resend` won't help if the number's
                    wrong. mailto link is the simplest escape hatch.
                  */}
                  <Pressable
                    onPress={() =>
                      void openEmail(SUPPORT_EMAIL, { subject: 'Login issues' })
                    }
                    style={styles.changeButton}
                    hitSlop={8}
                    accessibilityRole="link"
                    accessibilityLabel="Email support about login issues"
                  >
                    <Text style={styles.helpLink}>Need help?</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
});

export default VerifyScreen;

function messageForCode(code: string, fallback: string): string {
  switch (code) {
    case 'pin_invalid':
      return 'That code is incorrect. Try again.';
    case 'pin_expired':
      return 'That code expired. Request a new one.';
    case 'pin_locked':
      return 'Too many wrong attempts. Wait 15 minutes and try again.';
    case 'rate_limited':
      return 'Too many requests. Please wait and try again.';
    default:
      return fallback || 'Could not verify. Please try again.';
  }
}

function maskContact(contact: string): string {
  if (contact.includes('@')) {
    const [local, domain] = contact.split('@');
    if (!local || !domain) return contact;
    const head = local.slice(0, 1);
    const tail = local.slice(-1);
    return `${head}${'*'.repeat(Math.max(1, local.length - 2))}${tail}@${domain}`;
  }
  if (contact.length <= 4) return contact;
  return `${'•'.repeat(contact.length - 4)}${contact.slice(-4)}`;
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.deepGreen },
  safe: { flex: 1 },
  flex: { flex: 1 },
  scroll: { flexGrow: 1, justifyContent: 'center', padding: spacing.lg },
  cardWrap: { width: '100%', alignItems: 'center' },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: colors.glassFrostFill,
    borderRadius: radii.lg,
    padding: spacing.xl,
    borderWidth: 1,
    borderColor: colors.glassFrostBorder,
    ...shadows.modal,
  },
  brand: { alignItems: 'center', marginBottom: spacing.xl, gap: spacing.sm },
  brandTile: { marginBottom: spacing.sm },
  heading: { textAlign: 'center' },
  subheading: {
    fontFamily: fonts.body,
    fontSize: fontSizes.base,
    lineHeight: 22,
    color: colors.textOnDarkMuted,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  pinWrap: { marginBottom: spacing.lg },
  spinner: { marginVertical: spacing.sm },
  error: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.dangerBright,
    marginBottom: spacing.base,
    textAlign: 'center',
  },
  actions: { alignItems: 'center', marginTop: spacing.lg, gap: spacing.md },
  resendButton: { paddingVertical: spacing.xs },
  resend: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.electricGreen,
  },
  resendDisabled: { color: colors.textOnDarkDisabled },
  changeButton: { paddingVertical: spacing.xs },
  change: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textOnDarkSubtle,
  },
  // Deliberately weighted like `resend` rather than the muted `change` links:
  // for a carrier-blocked user this is the only action on the screen that can
  // actually work, so it should not read as a footnote.
  emailFallback: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.electricGreen,
  },
  emailFallbackLead: {
    fontFamily: fonts.body,
    color: colors.textOnDarkSubtle,
  },
  emailSent: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textOnDarkSubtle,
    textAlign: 'center',
    paddingVertical: spacing.xs,
  },
  helpLink: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.textOnDarkSubtle,
    textDecorationLine: 'underline',
  },
});
